import { useEffect, useState } from "react";

function diff(endsAt) {
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return { ms, days, hours, minutes, seconds };
}

function Cell({ value, label, big = false, alert = false }) {
  return (
    <div className="flex flex-col items-center">
      <span
        className={`font-display tabular-nums leading-none transition-colors ${
          alert ? "text-red-600" : "text-black"
        } ${big ? "text-5xl sm:text-6xl" : "text-3xl sm:text-4xl"}`}
      >
        {String(value).padStart(2, "0")}
      </span>
      <span
        className={`mt-2 text-[10px] font-medium uppercase tracking-[0.25em] transition-colors ${
          alert ? "text-red-600/70" : "text-black/45"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

const FINAL_HOUR_MS = 3_600_000;

export default function Countdown({ endsAt, compact = false }) {
  const [t, setT] = useState(() => (endsAt ? diff(endsAt) : null));

  useEffect(() => {
    if (!endsAt) return;
    setT(diff(endsAt));
    const id = setInterval(() => setT(diff(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  if (!endsAt || !t) return null;

  if (t.ms === 0) {
    return (
      <span className="inline-block px-3 py-1 rounded-full bg-black/5 text-black/55 text-[10px] font-medium uppercase tracking-[0.25em]">
        Głosowanie zamknięte
      </span>
    );
  }

  const finalHour = t.ms > 0 && t.ms <= FINAL_HOUR_MS;

  if (compact) {
    return (
      <div
        data-testid="countdown"
        className={`inline-flex items-center gap-3 px-4 py-2 rounded-full shadow-sm border ${
          finalHour
            ? "bg-red-50 border-red-200 animate-[finalPulse_1.6s_ease-in-out_infinite]"
            : "bg-white border-black/5"
        }`}
      >
        <span
          className={`text-[10px] font-medium uppercase tracking-[0.25em] ${
            finalHour ? "text-red-600" : "text-brand-green"
          }`}
        >
          {finalHour ? "FINAL HOUR" : "Do końca"}
        </span>
        <span
          className={`font-display text-base tabular-nums ${
            finalHour ? "text-red-700" : "text-black"
          }`}
        >
          {t.days}d · {String(t.hours).padStart(2, "0")}:
          {String(t.minutes).padStart(2, "0")}:{String(t.seconds).padStart(2, "0")}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="countdown" className="flex flex-col items-center">
      {finalHour ? (
        <p
          data-testid="final-hour-badge"
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-600 text-[10px] font-bold uppercase tracking-[0.35em] mb-5 animate-[finalPulse_1.4s_ease-in-out_infinite]"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600" />
          </span>
          Final call
        </p>
      ) : (
        <p className="text-[10px] font-medium uppercase tracking-[0.35em] text-black/40 mb-5">
          Do końca kampanii
        </p>
      )}

      <div className="flex items-start gap-5 sm:gap-7">
        <Cell value={t.days} label="dni" big alert={finalHour} />
        <span className={`font-display text-3xl sm:text-4xl leading-none mt-1 ${finalHour ? "text-red-300" : "text-black/15"}`}>:</span>
        <Cell value={t.hours} label="godz" big alert={finalHour} />
        <span className={`font-display text-3xl sm:text-4xl leading-none mt-1 ${finalHour ? "text-red-300" : "text-black/15"}`}>:</span>
        <Cell value={t.minutes} label="min" big alert={finalHour} />
        <span className={`font-display text-3xl sm:text-4xl leading-none mt-1 ${finalHour ? "text-red-300" : "text-black/15"}`}>:</span>
        <Cell value={t.seconds} label="sek" big alert={finalHour} />
      </div>
    </div>
  );
}
